import { Injectable } from '@angular/core';
import { Route, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AppConstants } from 'src/app/commons/app-constants';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { PermissionService } from './permission.service';
import { InventoryModuleService } from './inventory-module/inventory-module.service';

/** One page a user can "live on" after login, in order of preference. */
export interface HomePage {
  /** App URL to open. */
  url: string;
  /** Top-level route path whose data.permissions decide access (read from the real route setup). */
  routePath: string;
  labelKey: string;
  icon: string;
  /** Extra check beyond permissions (the Inventory module switch). */
  module?: 'inventory';
}

/**
 * Smart landing (SMART_LANDING_PLAN.md, 2026-09-28): the first page a user can really open —
 * Dashboard → Tally sheets → Consignments → Inventory; none → null (welcome screen).
 * Sales staff are already sent to Sales CRM by the server at login, so CRM is not in this list.
 *
 * Never copies permission rules: each page's rule is read from the actual route config
 * (route.data.permissions — the very thing PermissionGuard checks), against the permission set
 * already in memory. So this and the guard can't disagree, and it costs no extra server call
 * beyond the one session sync the guard already shares.
 */
@Injectable({ providedIn: 'root' })
export class HomeRouteService {
  static readonly PAGES: HomePage[] = [
    { url: '/dashboard-v7', routePath: 'dashboard-v7', labelKey: 'home.page.dashboard', icon: 'pi-chart-bar' },
    { url: '/new-tallysheet', routePath: 'new-tallysheet', labelKey: 'home.page.tallySheets', icon: 'pi-list' },
    { url: '/shipments', routePath: 'shipments', labelKey: 'home.page.consignments', icon: 'pi-send' },
    { url: '/inventory', routePath: 'inventory', labelKey: 'home.page.inventory', icon: 'pi-box', module: 'inventory' },
  ];

  constructor(
    private router: Router,
    private permissionService: PermissionService,
    private inventoryModule: InventoryModuleService,
    private http: HttpClient,
  ) {}

  /** "Request access": tells the company owner + admins which page this member asked for. */
  requestAccess(page: string | null, pageLabel: string | null): Observable<{ sent: boolean }> {
    return this.http.post<{ sent: boolean }>(`${AppConstants.API_URL}api/v1/access-requests`, { page, pageLabel });
  }

  /** The home-list page a URL belongs to (for naming it on the restricted page). */
  static pageForUrl(url: string | null | undefined): HomePage | null {
    const path = (url ?? '').split('?')[0];
    if (!path) return null;
    if (HomeRouteService.isLandingPath(path.replace(/^\//, ''))) return HomeRouteService.PAGES[0];
    return HomeRouteService.PAGES.find(p => path === p.url || path.startsWith(p.url + '/')) ?? null;
  }

  /** The routes that count as the automatic "landing" (dashboard or its old aliases). A refusal
   *  there should quietly send the user home, not to "Access restricted". */
  static isLandingPath(path: string | undefined | null): boolean {
    return !!path && path.startsWith('dashboard');
  }

  /** First page the user can open, or null when none (→ welcome screen). */
  resolveHome(): Observable<string | null> {
    return this.allowedPages().pipe(map(pages => pages.length ? pages[0].url : null));
  }

  /** Every home-list page the user can open, in order (the restricted page lists these). */
  allowedPages(): Observable<HomePage[]> {
    return this.permissionService.syncSessionForGuard().pipe(
      switchMap(() => {
        const rbacOn = this.permissionService.isRbacEnabledSnapshot();
        const perms = this.permissionService.getSnapshot();
        const byPerm = HomeRouteService.PAGES.filter(p => !rbacOn || this.allowed(p, perms));
        if (!byPerm.some(p => p.module === 'inventory')) return of(byPerm);
        return this.inventoryModule.checkVisible().pipe(
          catchError(() => of(false)),
          map(visible => byPerm.filter(p => p.module !== 'inventory' || visible)),
        );
      }),
      catchError(() => of([] as HomePage[])),
    );
  }

  /** For admins editing a role (no module check — that is per company, not per role): the page a
   *  user with exactly these permission codes would start on, or null for "no page yet". */
  startPageFor(codes: Set<string>): HomePage | null {
    return HomeRouteService.PAGES.find(p => this.allowed(p, codes)) ?? null;
  }

  private allowed(p: HomePage, perms: Set<string>): boolean {
    const required = this.requiredFor(p.routePath);
    return required.length === 0 || required.some(code => perms.has(code));
  }

  private requiredFor(routePath: string): string[] {
    const route: Route | undefined = this.router.config.find(r => r.path === routePath);
    return (route?.data?.['permissions'] as string[] | undefined) ?? [];
  }
}
